import jsPDF from 'jspdf';
import type { CardPrintTemplate } from '@citizen-walk/shared-types';
import type { ResolvedCardTheme } from './cardTheme';
import { sortFields, type CardPrintField } from './cardLayout';
import { rasterizeDecoration } from './decorationRasterizer';
import { prepareContainPhoto } from './photoPreparation';
import { generateQrDataUrl } from './QrCodeOverlay';
import { loadFont, loadFontBold, registerFont } from './fontLoader';
import { CARD_FONT_LICENSE, resolveCardFont } from './fontRegistry';
import {
  buildPostQueue,
  clampQrSize,
  DEFAULT_FIELD_GAP_MM,
  type CropMarks,
  type FillBehavior,
} from './jobConfig';
import { isOverlayOnlyField, resolveCategoryBadgeStyle } from './builtinFields';
import {
  BOLD_STROKE_RATIO,
  buildFieldDisplayText,
  effectiveMaxLines,
  fitMode,
  LINE_HEIGHT,
  MIN_FIT_FONT_PT,
  TEXT_TOP_INSET_MM,
  type FitMode,
} from './fieldText';
import { getCardPostBadgeLabel, type CardPrintPost } from './useCardPrintPosts';

/**
 * AW6 — A4 名刺 10 面付け PDF 生成 (jspdf 直書き)。
 *
 * old2 `buildCardPdf.ts` + `cardDrawer.ts` を v2 に移植し、Critical/High/Medium を網羅:
 *   - 日本語フォント埋込 (Noto Sans JP、fontLoader)。フォント取得失敗時は helvetica fallback で続行。
 *   - 写真 contain 焼込 (prepareContainPhoto、theme.layout.photoWidth_mm / photoSide)
 *   - decoration SVG → PNG raster で addImage (opacity / secondary tint 対応)
 *   - startIndex (使い掛けシートの先頭セルスキップ)
 *   - cropMarks (none / corners / lines)
 *   - fillBehavior (空セル / 繰り返しでページを埋める)
 *   - adminNote (全カード共通の追記欄、__adminNote 擬似 field)
 *   - categoryColors (extraFields.category ラベル → 色のバッジ)
 *   - 生成進捗 onProgress(done, total)
 *
 * v2 差分:
 *   - 旧 `config.theme` 一体型 → v2 は `template`(fields source) + `theme`(ResolvedCardTheme) +
 *     job config 系オプション (adminNote / cropMarks / fillBehavior / startIndex / fieldGap_mm) を
 *     分離して受ける。
 *   - QR は投稿位置の Google Maps URL (old2 `buildMapsUrl` 踏襲) を呼び出し側 (CardPrintPage) で
 *     生成し qrUrls で渡す。位置情報の無い投稿は null (QR なし)。
 */

const SHEET_W_MM = 210;
const SHEET_H_MM = 297;
const CARD_W_MM = 91;
const CARD_H_MM = 55;
const MARGIN_LEFT_MM = 14;
const MARGIN_TOP_MM = 11;
const COLS = 2;
const PER_SHEET = 10;
const PT_TO_MM = 25.4 / 72;
/** QR をカード右下に置くときの余白 (mm)。 */
const QR_MARGIN_MM = 1.5;

export interface CellPosition {
  index: number;
  col: number;
  row: number;
  x_mm: number;
  y_mm: number;
}

/** A4 シート内 10 セル の左上 mm 座標を返す (pure helper、test 容易)。 */
export function buildCellPositions(): CellPosition[] {
  return Array.from({ length: PER_SHEET }, (_, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    return {
      index: i,
      col,
      row,
      x_mm: MARGIN_LEFT_MM + col * CARD_W_MM,
      y_mm: MARGIN_TOP_MM + row * CARD_H_MM,
    };
  });
}

export interface BuildPdfOptions {
  posts: CardPrintPost[];
  /**
   * 既定 fields の供給元 (任意)。`fields` を明示指定する運用 (テンプレ無しの自由レイアウト
   * を含む) では未指定でよい。未指定かつ `fields` も無ければ空 fields。
   */
  template?: Pick<CardPrintTemplate, 'fields'>;
  theme: ResolvedCardTheme;
  /** ジョブ用に編集された表示項目。未指定なら template.fields を採用。 */
  fields?: CardPrintField[];
  /** post.id → 絶対 URL (Storage download URL 解決済)。null は写真なし。 */
  photoUrls?: Record<string, string | null>;
  /** post.id → deep link (例: `https://app.example.com/m/<urlToken>`)。null/空は QR なし。 */
  qrUrls?: Record<string, string | null>;
  /** データ設定 postFields の key→日本語名 (showLabel 表示用)。未指定は {}。 */
  labelMap?: Record<string, string>;
  /** AI 分類コード→日本語名 (style.resolveCodes の項目で使用)。未指定は {}。 */
  codeToName?: Record<string, string>;
  /** post.sessionId → セッション名 (組み込み field sessionName 表示用)。未指定は {}。 */
  sessionNameMap?: Record<string, string>;
  /** 先頭 cell index (0..9)。使い掛けシートの再利用用。 */
  startIndex?: number;
  /** 全カード共通の追記テキスト (__adminNote field がある場合に印字)。 */
  adminNote?: string;
  cropMarks?: CropMarks;
  fillBehavior?: FillBehavior;
  /** 表示項目どうしの行間 (mm)。未指定は既定 0.6。0..8 にクランプ。 */
  fieldGap_mm?: number;
  /** QR コードの一辺 (mm)。未指定は既定 8。MIN..MAX_QR_SIZE_MM にクランプ。 */
  qrSize_mm?: number;
  onProgress?: (done: number, total: number) => void;
}

/**
 * input options を normalize (test 容易性: defaults / 範囲外 startIndex の挙動を pure に確認)。
 */
export function normalizePdfOptions(
  opts: BuildPdfOptions,
): Required<Omit<BuildPdfOptions, 'onProgress'>> & { onProgress: BuildPdfOptions['onProgress'] } {
  const startIndex = Math.max(0, Math.min(PER_SHEET - 1, opts.startIndex ?? 0));
  const template = opts.template ?? { fields: [] };
  return {
    posts: opts.posts,
    template,
    theme: opts.theme,
    fields: opts.fields ?? sortFields(template.fields).map((f) => ({ ...f })),
    photoUrls: opts.photoUrls ?? {},
    qrUrls: opts.qrUrls ?? {},
    labelMap: opts.labelMap ?? {},
    codeToName: opts.codeToName ?? {},
    sessionNameMap: opts.sessionNameMap ?? {},
    startIndex,
    adminNote: opts.adminNote ?? '',
    cropMarks: opts.cropMarks ?? 'none',
    fillBehavior: opts.fillBehavior ?? 'leave-empty',
    fieldGap_mm: Math.max(0, Math.min(8, opts.fieldGap_mm ?? DEFAULT_FIELD_GAP_MM)),
    qrSize_mm: clampQrSize(opts.qrSize_mm),
    onProgress: opts.onProgress,
  };
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return { r: 0, g: 0, b: 0 };
  return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff };
}

function setFill(pdf: jsPDF, hex: string): void {
  const c = hexToRgb(hex);
  pdf.setFillColor(c.r, c.g, c.b);
}
function setDraw(pdf: jsPDF, hex: string): void {
  const c = hexToRgb(hex);
  pdf.setDrawColor(c.r, c.g, c.b);
}
function setText(pdf: jsPDF, hex: string): void {
  const c = hexToRgb(hex);
  pdf.setTextColor(c.r, c.g, c.b);
}

interface PdfFontState {
  family: string;
  hasJp: boolean;
  /** 本物の Bold ウェイトを登録済みか (true=実太字、false=輪郭線で擬似太字)。 */
  hasBold: boolean;
}

function applyFont(pdf: jsPDF, font: PdfFontState, bold: boolean): void {
  if (font.hasJp) pdf.setFont(font.family, bold ? 'bold' : 'normal');
  else pdf.setFont('helvetica', bold ? 'bold' : 'normal');
}

/**
 * テキストを描画する。
 *
 * - `bold` かつ本物の Bold ウェイトが利用可能 (hasRealBold=true): 呼び出し側が applyFont で
 *   'bold' スタイル (= 登録済みの Bold フォント) を選択済みのため、通常の塗り (fill) で実太字を描く。
 * - `bold` だが Bold フォントが無い (hasRealBold=false、等幅など): 塗り＋輪郭線 (fillThenStroke) で
 *   擬似太字化する (preview の `-webkit-text-stroke` と同じ BOLD_STROKE_RATIO・字幅 Regular のまま)。
 */
export function drawText(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  fontSizePt: number,
  hexColor: string,
  bold: boolean,
  hasRealBold: boolean,
): void {
  setText(pdf, hexColor);
  const opts: Record<string, unknown> = { baseline: 'top' };
  if (bold && !hasRealBold) {
    setDraw(pdf, hexColor);
    pdf.setLineWidth(fontSizePt * PT_TO_MM * BOLD_STROKE_RATIO);
    opts.renderingMode = 'fillThenStroke';
  } else {
    opts.renderingMode = 'fill';
  }
  pdf.text(text, x, y, opts as Parameters<typeof pdf.text>[3]);
}

function drawCornerMarks(pdf: jsPDF): void {
  const len = 3;
  setDraw(pdf, '#b0b0b0');
  pdf.setLineWidth(0.1);
  for (const p of buildCellPositions()) {
    const corners = [
      { x: p.x_mm, y: p.y_mm, dx: -1, dy: -1 },
      { x: p.x_mm + CARD_W_MM, y: p.y_mm, dx: 1, dy: -1 },
      { x: p.x_mm, y: p.y_mm + CARD_H_MM, dx: -1, dy: 1 },
      { x: p.x_mm + CARD_W_MM, y: p.y_mm + CARD_H_MM, dx: 1, dy: 1 },
    ];
    for (const c of corners) {
      const x1 = c.dx < 0 ? c.x - len : c.x;
      const x2 = c.dx < 0 ? c.x : c.x + len;
      pdf.line(x1, c.y, x2, c.y);
      const y1 = c.dy < 0 ? c.y - len : c.y;
      const y2 = c.dy < 0 ? c.y : c.y + len;
      pdf.line(c.x, y1, c.x, y2);
    }
  }
}

export function drawDashedLines(pdf: jsPDF): void {
  setDraw(pdf, '#b0b0b0');
  pdf.setLineWidth(0.1);
  const anyPdf = pdf as unknown as {
    setLineDashPattern?: (pattern: number[], phase: number) => void;
  };
  anyPdf.setLineDashPattern?.([0.5, 0.5], 0);
  // 横 (y) だけでなく縦 (x) のカットラインも引く。
  // 旧実装は ys のみ描画していたため PDF に横罫線しか出ず、断裁できなかった (old2 から踏襲した不具合)。
  const ys = new Set<number>();
  const xs = new Set<number>();
  for (const p of buildCellPositions()) {
    ys.add(p.y_mm);
    ys.add(p.y_mm + CARD_H_MM);
    xs.add(p.x_mm);
    xs.add(p.x_mm + CARD_W_MM);
  }
  for (const y of ys) pdf.line(0, y, SHEET_W_MM, y); // 横罫線 (シート全幅)
  for (const x of xs) pdf.line(x, 0, x, SHEET_H_MM); // 縦罫線 (シート全高)
  anyPdf.setLineDashPattern?.([], 0);
}

function drawCropMarks(pdf: jsPDF, cropMarks: CropMarks): void {
  if (cropMarks === 'corners') drawCornerMarks(pdf);
  else if (cropMarks === 'lines') drawDashedLines(pdf);
}

function drawCategoryBadge(
  pdf: jsPDF,
  font: PdfFontState,
  label: string,
  theme: ResolvedCardTheme,
  x: number,
  y: number,
  badgeStyle: { fontSize: number; bold: boolean },
): void {
  const color = theme.categoryColors[label] ?? theme.accent;
  const { fontSize, bold } = badgeStyle;
  applyFont(pdf, font, bold);
  pdf.setFontSize(fontSize);
  const labelWidth = pdf.getTextWidth(label);
  const padX = 1.2;
  const padY = 0.5;
  const fontMm = fontSize * PT_TO_MM;
  const w = labelWidth + padX * 2;
  const h = fontMm * 1.1 + padY * 2;
  setFill(pdf, color);
  const anyPdf = pdf as unknown as {
    roundedRect?: (
      x: number,
      y: number,
      w: number,
      h: number,
      rx: number,
      ry: number,
      style: string,
    ) => void;
  };
  if (anyPdf.roundedRect) anyPdf.roundedRect(x, y, w, h, 0.6, 0.6, 'F');
  else pdf.rect(x, y, w, h, 'F');
  // バッジ文字は白。bold は実 Bold (あれば) or 輪郭線 (drawText)。
  drawText(pdf, label, x + padX, y + padY, fontSize, '#ffffff', bold, font.hasBold);
}

async function drawDecoration(
  pdf: jsPDF,
  d: ResolvedCardTheme['decorations'][number],
  theme: ResolvedCardTheme,
  cardX: number,
  cardY: number,
): Promise<void> {
  const png = await rasterizeDecoration(d, theme);
  if (!png) return;
  const opacity = d.opacity ?? 1;
  const anyPdf = pdf as unknown as {
    setGState?: (g: unknown) => void;
    GState?: new (o: unknown) => unknown;
  };
  if (opacity < 1 && anyPdf.setGState && anyPdf.GState) {
    anyPdf.setGState(new anyPdf.GState({ opacity }));
  }
  try {
    pdf.addImage(png, 'PNG', cardX + d.x_mm, cardY + d.y_mm, d.width_mm, d.height_mm);
  } catch (e) {
    console.warn('[card-print] addImage(decoration) failed', e);
  }
  if (opacity < 1 && anyPdf.setGState && anyPdf.GState) {
    anyPdf.setGState(new anyPdf.GState({ opacity: 1 }));
  }
}

/** jspdf 上で fit='shrink' のフォントサイズを実測 (splitTextToSize) で求める。 */
function fitFontSizePdf(
  pdf: jsPDF,
  font: PdfFontState,
  bold: boolean,
  value: string,
  textW: number,
  baseFontSize: number,
  maxLines: number,
  fit: FitMode,
): number {
  if (fit !== 'shrink') return baseFontSize;
  let pt = baseFontSize;
  while (pt > MIN_FIT_FONT_PT) {
    applyFont(pdf, font, bold);
    pdf.setFontSize(pt);
    const lines = pdf.splitTextToSize(value, textW);
    const count = Array.isArray(lines) ? lines.length : 1;
    if (count <= maxLines) break;
    pt = Math.round((pt - 0.5) * 10) / 10;
  }
  return Math.max(MIN_FIT_FONT_PT, pt);
}

/** 末尾行を「… を付けても textW を超えない」ところまで削ってから … を付ける (現在のフォント前提)。 */
function ellipsizeToWidth(pdf: jsPDF, line: string, widthMm: number): string {
  const ell = '…';
  if (pdf.getTextWidth(line + ell) <= widthMm) return line + ell;
  let chars = Array.from(line);
  while (chars.length > 0 && pdf.getTextWidth(chars.join('') + ell) > widthMm) {
    chars = chars.slice(0, -1);
  }
  return chars.join('') + ell;
}

async function drawCell(
  pdf: jsPDF,
  font: PdfFontState,
  post: CardPrintPost,
  position: CellPosition,
  options: ReturnType<typeof normalizePdfOptions>,
): Promise<void> {
  const { theme, photoUrls, qrUrls, fields, adminNote, labelMap, codeToName, sessionNameMap } =
    options;
  const fieldGap = options.fieldGap_mm;
  const layout = theme.layout;
  const isLeft = layout.photoSide === 'left';
  const photoX = isLeft ? position.x_mm : position.x_mm + CARD_W_MM - layout.photoWidth_mm;

  // 1. background
  setFill(pdf, theme.backgroundColor);
  pdf.rect(position.x_mm, position.y_mm, CARD_W_MM, CARD_H_MM, 'F');

  // 2. background-pattern decorations
  for (const d of theme.decorations.filter((x) => x.slot === 'background-pattern')) {
    await drawDecoration(pdf, d, theme, position.x_mm, position.y_mm);
  }

  // 3. photo (contain)
  const photoUrl = photoUrls[post.id] ?? null;
  if (photoUrl && layout.photoWidth_mm > 0) {
    const photoDataUrl = await prepareContainPhoto(photoUrl, {
      widthMm: layout.photoWidth_mm,
      heightMm: CARD_H_MM,
      background: theme.backgroundColor,
    });
    if (photoDataUrl) {
      try {
        pdf.addImage(photoDataUrl, 'JPEG', photoX, position.y_mm, layout.photoWidth_mm, CARD_H_MM);
      } catch (e) {
        console.warn('[card-print] addImage(photo) failed', e);
      }
    }
  }

  // 4. bands (header/footer/side) over photo
  for (const d of theme.decorations.filter((x) =>
    ['header-band', 'footer-band', 'left-side-band'].includes(x.slot),
  )) {
    await drawDecoration(pdf, d, theme, position.x_mm, position.y_mm);
  }
  // 5. corner decorations
  for (const d of theme.decorations.filter((x) =>
    ['corner-top-left', 'corner-top-right', 'corner-bottom-left', 'corner-bottom-right'].includes(
      x.slot,
    ),
  )) {
    await drawDecoration(pdf, d, theme, position.x_mm, position.y_mm);
  }
  // 5b. title-icon decoration — テキスト/バッジを最前面にするため、装飾は全てテキストより先 (下) に描画。
  for (const d of theme.decorations.filter((x) => x.slot === 'title-icon')) {
    await drawDecoration(pdf, d, theme, position.x_mm, position.y_mm);
  }

  // 6. text fields
  const showCategory = fields.some((f) => f.key === 'category');
  const textX = isLeft
    ? position.x_mm + layout.photoWidth_mm + layout.textPadding_mm
    : position.x_mm + layout.textPadding_mm;
  const textW = CARD_W_MM - layout.photoWidth_mm - layout.textPadding_mm * 2;
  let cursorY = position.y_mm + TEXT_TOP_INSET_MM;
  const bottomLimit = position.y_mm + CARD_H_MM - 1.5;

  for (const f of fields) {
    if (isOverlayOnlyField(f)) continue; // category=バッジ / qrCode=QR overlay で別途描画
    const isTitle = f.key === 'title';
    const baseFontSize =
      f.style?.fontSize ?? (isTitle ? layout.titleFontSize_pt : layout.bodyFontSize_pt);
    const bold = !!f.style?.bold || isTitle;
    const maxLines = effectiveMaxLines(f, 2);
    const fit = fitMode(f);
    const value = buildFieldDisplayText(post, f, {
      adminNote,
      labelMap,
      codeToName,
      sessionNameMap,
    });
    if (!value) {
      // 空値: fit='fixed' は preview と同じく maxLines 行分の高さを確保 (整列維持)。
      // shrink/grow は行ごとスキップ (高さも消費しない)。
      if (fit === 'fixed') {
        const emptyLineMm = baseFontSize * PT_TO_MM * LINE_HEIGHT;
        cursorY = Math.min(bottomLimit, cursorY + maxLines * emptyLineMm) + fieldGap;
        if (cursorY > bottomLimit) break;
      }
      continue;
    }
    const fontSize = fitFontSizePdf(pdf, font, bold, value, textW, baseFontSize, maxLines, fit);
    applyFont(pdf, font, bold);
    pdf.setFontSize(fontSize);
    const textColor = f.style?.color ?? (isTitle ? theme.textHeading : theme.textColor);
    const rawLines = pdf.splitTextToSize(value, textW);
    const allLines: string[] = Array.isArray(rawLines) ? rawLines : [String(rawLines)];
    const shown = allLines.slice(0, maxLines);
    // maxLines を超えて切り詰めた場合は末尾行を textW 内に収めてから … を付与
    // (… 付与で textW を超えて写真/枠へはみ出すのを防ぐ)。
    if (allLines.length > shown.length && shown.length > 0) {
      shown[shown.length - 1] = ellipsizeToWidth(pdf, shown[shown.length - 1]!, textW);
    }
    const lineHeightMm = fontSize * PT_TO_MM * LINE_HEIGHT;
    let drawn = 0;
    for (const line of shown) {
      if (cursorY + lineHeightMm > bottomLimit) break;
      drawText(pdf, line, textX, cursorY, fontSize, textColor, bold, font.hasBold);
      cursorY += lineHeightMm;
      drawn++;
    }
    // fit='fixed' は maxLines 行分の高さを常に確保 (カード間の整列を安定させる)。
    if (fit === 'fixed' && drawn < maxLines) {
      cursorY = Math.min(bottomLimit, cursorY + (maxLines - drawn) * lineHeightMm);
    }
    cursorY += fieldGap;
    if (cursorY > bottomLimit) break;
  }

  // 8. border
  if (theme.border.width_mm > 0) {
    setDraw(pdf, theme.border.color);
    pdf.setLineWidth(theme.border.width_mm);
    pdf.rect(position.x_mm, position.y_mm, CARD_W_MM, CARD_H_MM, 'S');
  }

  // 9. category badge (top-left)
  if (showCategory) {
    // バッジの対象項目はバッジのスロット (key='category') の fieldPath (既定 投稿タイプ)。
    const catField = fields.find((f) => f.key === 'category');
    const catLabel = getCardPostBadgeLabel(post, catField?.fieldPath);
    if (catLabel) {
      // バッジのフォントサイズ・太字は category フィールドの書式 (style) を反映 (未設定は 7pt・太字)。
      const badgeStyle = resolveCategoryBadgeStyle(catField);
      drawCategoryBadge(
        pdf,
        font,
        catLabel,
        theme,
        position.x_mm + 1.2,
        position.y_mm + 1.2,
        badgeStyle,
      );
    }
  }

  // 10. QR (bottom-right)
  const qrUrl = qrUrls[post.id];
  if (qrUrl) {
    const qrSize = options.qrSize_mm;
    // ラスタ解像度を mm に比例させる (大きい QR でもくっきり、にじみに強く)。最低 256px。
    const qrDataUrl = await generateQrDataUrl(qrUrl, {
      darkColor: theme.textColor,
      lightColor: theme.backgroundColor,
      widthPx: Math.max(256, Math.round(qrSize * 45)),
    });
    if (qrDataUrl) {
      const qrX = position.x_mm + CARD_W_MM - qrSize - QR_MARGIN_MM;
      const qrY = position.y_mm + CARD_H_MM - qrSize - QR_MARGIN_MM;
      setFill(pdf, '#ffffff');
      pdf.rect(qrX - 0.4, qrY - 0.4, qrSize + 0.8, qrSize + 0.8, 'F');
      try {
        pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      } catch (e) {
        console.warn('[card-print] addImage(QR) failed', e);
      }
    }
  }
}

/**
 * A4 10 面付け PDF を生成。
 * 戻り値の jsPDF instance に対し `save(filename)` または `output('blob')` を呼んでダウンロード。
 */
export async function buildCardPdf(opts: BuildPdfOptions): Promise<jsPDF> {
  const options = normalizePdfOptions(opts);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  // 選択フォント (theme.fontFamily の font id、既定ゴシック) を埋め込む。
  // Regular に加え boldUrl があれば本物の Bold も埋め込む (太字対応)。
  // 失敗時は helvetica fallback で続行 — 英数字のみ正常表示。
  const fontDef = resolveCardFont(options.theme.fontFamily);
  const font: PdfFontState = { family: fontDef.pdfFamily, hasJp: false, hasBold: false };
  try {
    const regular = await loadFont(fontDef);
    let bold: Awaited<ReturnType<typeof loadFontBold>> = null;
    try {
      bold = await loadFontBold(fontDef);
    } catch (e) {
      console.warn('[card-print] Bold フォント読込失敗、擬似太字で続行', e);
    }
    registerFont(pdf, regular, bold);
    pdf.setFont(regular.family, 'normal');
    font.hasJp = true;
    font.hasBold = !!bold;
  } catch (e) {
    console.warn('[card-print] フォント読込失敗、helvetica で続行', e);
  }

  // OFL 1.1 §2: 埋め込みフォントの著作権/ライセンスを PDF メタデータに記録 (利用者が閲覧可能)。
  if (font.hasJp) {
    pdf.setProperties({
      creator: 'CitizenWalk',
      subject: `Embedded font: ${fontDef.label} — ${fontDef.copyright} (${CARD_FONT_LICENSE})`,
      keywords: CARD_FONT_LICENSE,
    });
  }

  const queue = buildPostQueue(options.posts, options.fillBehavior, PER_SHEET);
  const positions = buildCellPositions();
  let cellIndex = options.startIndex;
  let done = 0;
  const total = queue.length;

  for (const post of queue) {
    if (cellIndex >= PER_SHEET) {
      drawCropMarks(pdf, options.cropMarks);
      pdf.addPage('a4', 'portrait');
      cellIndex = 0;
    }
    await drawCell(pdf, font, post, positions[cellIndex]!, options);
    cellIndex++;
    done++;
    options.onProgress?.(done, total);
  }
  drawCropMarks(pdf, options.cropMarks);

  return pdf;
}

/**
 * 生成済 PDF をブラウザ DL する小 helper (jsPDF.save の薄ラッパ)。
 */
export function downloadPdf(pdf: jsPDF, filename: string): void {
  pdf.save(filename);
}
